import axios from "axios";
import { jwtDecode } from "jwt-decode";
import type { AssignProjectRole, AssignTeamRole, CreateProject, CreateTeam, CreateUser, User } from "@domain/schemas/BI";
import { ProjectRoleSchema, TeamRoleSchema } from "@domain/schemas/BI";
import type { MyProject, MyTeam, PagedUserSummaryList, ProjectDetail, ProjectRoleName, TeamDetail, TeamRoleName, UserSummary } from "@domain/schemas/workspace";
import { ProjectRoles, TeamRoles } from "@domain/schemas/workspace";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";

export const RBAC_DEV_TEAM_NAME = "RBAC Dev Tools Team";
export const RBAC_DEV_PROJECT_NAME = "RBAC Dev Tools Project";
// Public, deliberately fixed credentials for disposable development accounts, never administrator credentials.
export const RBAC_DEV_PASSWORD = "Rbac-Dev-Only1!";

export function isRbacDevAdminSession(token: string | null): boolean {
  if (!token) return false;
  const claims = jwtDecode<Record<string, unknown>>(token);
  // eslint-disable-next-line unicorn/prefer-https -- .NET ClaimTypes.Role is an exact claim identifier, not a network URL.
  const roleClaims = [claims.role, claims.roles, claims["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"]];
  return roleClaims.some((roles) => roles === "Admin" || (Array.isArray(roles) && roles.includes("Admin")));
}

interface PersonaDefinition {
  username: string;
  displayName: string;
  password: string;
  teamRole: TeamRoleName;
  projectRole: ProjectRoleName;
}

export const RBAC_DEV_PERSONAS: PersonaDefinition[] = [
  { username: "rbac-dev-manager", displayName: "RBAC Dev Tools Manager", password: RBAC_DEV_PASSWORD, teamRole: TeamRoles.Manager, projectRole: ProjectRoles.Manager },
  { username: "rbac-dev-project-lead", displayName: "RBAC Dev Tools Project Lead", password: RBAC_DEV_PASSWORD, teamRole: TeamRoles.ProjectLead, projectRole: ProjectRoles.Reader },
  { username: "rbac-dev-operator", displayName: "RBAC Dev Tools Operator", password: RBAC_DEV_PASSWORD, teamRole: TeamRoles.Member, projectRole: ProjectRoles.Operator },
  { username: "rbac-dev-reader", displayName: "RBAC Dev Tools Reader", password: RBAC_DEV_PASSWORD, teamRole: TeamRoles.Member, projectRole: ProjectRoles.Reader },
];

export interface DevPersona extends PersonaDefinition {
  id: string;
}

export interface RbacDevScenario {
  personas: DevPersona[];
  team: MyTeam | undefined;
  project: MyProject | undefined;
  teams: MyTeam[];
  projects: MyProject[];
}

const teamRoles = {
  [TeamRoles.Manager]: TeamRoleSchema.Manager,
  [TeamRoles.ProjectLead]: TeamRoleSchema.ProjectLead,
  [TeamRoles.Member]: TeamRoleSchema.Member,
} satisfies Record<TeamRoleName, TeamRoleSchema>;

const projectRoles = {
  [ProjectRoles.Manager]: ProjectRoleSchema.Manager,
  [ProjectRoles.Operator]: ProjectRoleSchema.Operator,
  [ProjectRoles.Reader]: ProjectRoleSchema.Reader,
} satisfies Record<ProjectRoleName, ProjectRoleSchema>;

export const getRbacDevErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail ?? error.response?.data?.title;
    return typeof detail === "string" ? detail : `${error.message}${error.response ? ` (HTTP ${error.response.status})` : ""}`;
  }
  return error instanceof Error ? error.message : String(error);
};

async function apiRequest<T>(token: string, method: "get" | "post" | "put" | "delete", path: string, data?: unknown): Promise<T> {
  const response = await axios.request<T>({
    method,
    url: `${runtimeConfig.API_BASE_URL}${path}`,
    data,
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
}

function requiredId(value: string | null | undefined, resource: string): string {
  if (!value) throw new Error(`The API response did not include an ID for the created ${resource}.`);
  return value;
}

function uniqueMatch<T>(items: T[], isMatch: (item: T) => boolean, resource: string): T | undefined {
  const found = items.filter((item) => isMatch(item));
  if (found.length > 1) throw new Error(`Multiple ${resource} records were found. Resolve the duplicate records before using the RBAC dev tools.`);
  return found[0];
}

export async function loadRbacDevScenario(token: string): Promise<RbacDevScenario> {
  const [firstPage, teams, projects] = await Promise.all([
    apiRequest<PagedUserSummaryList>(token, "get", "/users?search=rbac-dev-&page=1&pageSize=100"),
    apiRequest<MyTeam[]>(token, "get", "/teams"),
    apiRequest<MyProject[]>(token, "get", "/projects"),
  ]);
  const users: UserSummary[] = [...firstPage.list];
  for (let page = 2; page <= firstPage.totalPages; page++) {
    const result = await apiRequest<PagedUserSummaryList>(token, "get", `/users?search=rbac-dev-&page=${page}&pageSize=100`);
    users.push(...result.list);
  }
  const personas: DevPersona[] = [];
  for (const definition of RBAC_DEV_PERSONAS) {
    const user = uniqueMatch(users, (candidate) => candidate.username.toLowerCase() === definition.username, `users named ${definition.username}`);
    if (!user) continue;
    if (user.displayName !== definition.displayName) {
      throw new Error(`The username ${definition.username} belongs to an unrecognized account. It will not be reused, changed or deleted.`);
    }
    personas.push({ ...definition, id: user.id });
  }
  const team = uniqueMatch(teams, (candidate) => candidate.name === RBAC_DEV_TEAM_NAME, "RBAC dev teams");
  const project = team ? uniqueMatch(projects, (candidate) => candidate.teamId === team.id && candidate.name === RBAC_DEV_PROJECT_NAME, "RBAC dev projects") : undefined;
  return { personas, team, project, teams, projects };
}

function assertDevTeam(team: TeamDetail, managerId: string): void {
  if (team.isDefault || team.name !== RBAC_DEV_TEAM_NAME || team.memberships.every((membership) => membership.userId !== managerId)) {
    throw new Error("The existing team is not owned by the RBAC dev manager. It will not be changed or deleted.");
  }
}

function assertDevProject(project: ProjectDetail, teamId: string, managerId: string): void {
  if (project.isDefault || project.name !== RBAC_DEV_PROJECT_NAME || project.teamId !== teamId || project.memberships.every((membership) => membership.userId !== managerId)) {
    throw new Error("The existing project is not owned by the RBAC dev manager in the expected team. It will not be changed or deleted.");
  }
}

export async function getOrCreateRbacDevScenario(token: string): Promise<RbacDevScenario> {
  const existing = await loadRbacDevScenario(token);
  const personas: DevPersona[] = [];
  for (const definition of RBAC_DEV_PERSONAS) {
    const found = existing.personas.find((persona) => persona.username === definition.username);
    if (found) {
      personas.push(found);
      continue;
    }
    const payload: CreateUser = { username: definition.username, password: definition.password, displayName: definition.displayName, email: `${definition.username}@example.invalid` };
    const created = await apiRequest<User>(token, "post", "/users", payload);
    personas.push({ ...definition, id: requiredId(created.id, "user") });
  }
  const managerId = personas[0].id;
  let team: TeamDetail;
  if (existing.team) {
    team = await apiRequest<TeamDetail>(token, "get", `/teams/${encodeURIComponent(existing.team.id)}`);
    assertDevTeam(team, managerId);
  } else {
    const payload: CreateTeam = { name: RBAC_DEV_TEAM_NAME, initialManagerUserId: managerId };
    team = await apiRequest<TeamDetail>(token, "post", "/teams", payload);
    requiredId(team.id, "team");
  }
  for (const persona of personas) {
    if (team.memberships.some((membership) => membership.userId === persona.id && membership.role === persona.teamRole)) continue;
    const payload: AssignTeamRole = { targetUserId: persona.id, role: teamRoles[persona.teamRole] };
    await apiRequest(token, "put", `/teams/${encodeURIComponent(team.id)}/members`, payload);
  }

  let project: ProjectDetail;
  if (existing.project) {
    project = await apiRequest<ProjectDetail>(token, "get", `/projects/${encodeURIComponent(existing.project.id)}`);
    assertDevProject(project, team.id, managerId);
  } else {
    const payload: CreateProject = { name: RBAC_DEV_PROJECT_NAME, initialAssignments: [{ userId: managerId, role: ProjectRoleSchema.Manager }] };
    project = await apiRequest<ProjectDetail>(token, "post", `/teams/${encodeURIComponent(team.id)}/projects`, payload);
    requiredId(project.id, "project");
  }
  for (const persona of personas) {
    if (project.memberships.some((membership) => membership.userId === persona.id && membership.role === persona.projectRole)) continue;
    const payload: AssignProjectRole = { targetUserId: persona.id, role: projectRoles[persona.projectRole] };
    await apiRequest(token, "put", `/projects/${encodeURIComponent(project.id)}/members`, payload);
  }
  return loadRbacDevScenario(token);
}

async function deleteResource(token: string, path: string): Promise<void> {
  try {
    await apiRequest(token, "delete", path);
  } catch (error) {
    if (!axios.isAxiosError(error) || error.response?.status !== 404) throw error;
  }
}

export async function cleanupRbacDevScenario(token: string): Promise<RbacDevScenario> {
  const existing = await loadRbacDevScenario(token);
  const manager = existing.personas.find((persona) => persona.username === RBAC_DEV_PERSONAS[0].username);
  if (existing.team) {
    if (!manager) throw new Error("The RBAC dev manager is missing. The existing team and its resources will not be deleted.");
    const team = await apiRequest<TeamDetail>(token, "get", `/teams/${encodeURIComponent(existing.team.id)}`);
    assertDevTeam(team, manager.id);
    if (existing.projects.some((project) => project.teamId === team.id && project.id !== existing.project?.id)) {
      throw new Error("The RBAC dev team contains additional projects. Remove or move them before cleaning up this scenario.");
    }
    if (existing.project) {
      const project = await apiRequest<ProjectDetail>(token, "get", `/projects/${encodeURIComponent(existing.project.id)}`);
      assertDevProject(project, team.id, manager.id);
      await deleteResource(token, `/teams/${encodeURIComponent(team.id)}/projects/${encodeURIComponent(project.id)}`);
    }
    await deleteResource(token, `/teams/${encodeURIComponent(team.id)}`);
  }
  const failures: string[] = [];
  for (const persona of existing.personas.toReversed()) {
    try {
      await deleteResource(token, `/users?userId=${encodeURIComponent(persona.id)}`);
    } catch (error) {
      failures.push(`${persona.username}: ${getRbacDevErrorMessage(error)}`);
    }
  }
  if (failures.length > 0) throw new Error(`Some development users could not be deleted:\n${failures.join("\n")}`);
  const remaining = await loadRbacDevScenario(token);
  if (remaining.personas.length > 0 || remaining.team || remaining.project) throw new Error("Some RBAC dev resources still exist. Refresh and retry cleanup.");
  return remaining;
}
